import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Home, LogOut, Briefcase } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getDriverWorks } from "../../store/slices/worksSlice";
import { Work } from "../../types/works";
import { DriverWorkCard } from "../../components/works/driver/DriverWorkCard";

/**
 * Driver-facing list of assigned works — mobile responsive, mirrors the
 * Shipments driver view. Drivers never create works here; they only open and
 * execute the ones dispatched to them. The backend scopes /works/driver/mine
 * to the authenticated driver, so no client-side driver filtering is needed.
 */
export const DriverWorksListPage: React.FC = () => {
  const { t } = useTranslation("works");
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { works } = useAppSelector((state) => state.works);
  const [activeTab, setActiveTab] = useState<"active" | "completed">("active");

  useEffect(() => {
    dispatch(getDriverWorks());
  }, [dispatch]);

  const filtered = useMemo(
    () =>
      works.filter((w) =>
        activeTab === "completed"
          ? w.status === "completed" || w.status === "cancelled"
          : w.status !== "completed" && w.status !== "cancelled",
      ),
    [works, activeTab],
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch {
      // logout failure is non-fatal
    }
  };

  const openWork = (work: Work) => navigate(`/works/${work._id}`);

  const activeCount = works.filter(
    (w) => w.status !== "completed" && w.status !== "cancelled",
  ).length;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="bg-white border-b border-[#ECEAE4] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#C97A3C] rounded-full flex items-center justify-center text-white font-semibold uppercase shrink-0">
              {user?.firstName?.[0] || "D"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#2B2F2E] truncate">{user?.fullName}</div>
              <div className="text-xs text-[#8A9590]">{t("driver.list.title")}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/driver-home")}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#5A6460] bg-white border border-[#E5E3DD] rounded-lg hover:bg-[#F5F4F0] transition-colors"
            >
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">{t("driver.home.back")}</span>
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#5A6460] bg-white border border-[#E5E3DD] rounded-lg hover:bg-[#F5F4F0] transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("driver.list.logout")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-5">
          <h1 className="text-2xl font-bold text-[#0B0F0E]">{t("driver.list.heading")}</h1>
          <p className="text-sm text-[#8A9590]">
            {t("driver.list.assignments", { count: activeCount })}
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#ECEAE4] mb-6">
          <nav className="-mb-px flex gap-6">
            {(["active", "completed"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? "border-[#C97A3C] text-[#C97A3C]"
                    : "border-transparent text-[#8A9590] hover:text-[#5A6460]"
                }`}
              >
                {t(`driver.list.tabs.${tab}`)}
              </button>
            ))}
          </nav>
        </div>

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-12 w-12 rounded-xl bg-[#FCEEE2] flex items-center justify-center text-[#C97A3C] mb-3">
              <Briefcase size={22} />
            </div>
            <p className="text-sm font-medium text-[#2B2F2E]">{t("driver.list.empty.title")}</p>
            <p className="text-sm text-[#8A9590] mt-1">{t("driver.list.empty.subtitle")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((work) => (
              <DriverWorkCard key={work._id} work={work} onOpen={openWork} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverWorksListPage;
